import { addShortcodeToEditor, getShortcodeWrapper } from "@/assets/utilities/tinymce/shortodeAddition";
import { getLiveChatMp4Template, getLiveChatMp4V2Template } from "@/assets/utilities/tinymce/shortcodes/templates";
import { parseToHtml } from "@/utility/parseToHtml";
import { serializeDynamicComponents, visualizeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
import { convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
import { traverseTree } from "@/utility/traverseTree";
import { findInvalidTelHrefsDeep } from "@/utility/validateLiveChatTelHrefs";
import i18n from "@/i18n";
import { showTelConfirmModal } from "@/assets/utilities/tinymce/helpers/telConfirmModal";
import { normalizeIds } from "@/components/shortcodes/livechatV2/treeGraph";
import { editLiveChatMp4V2Config } from "@/assets/utilities/tinymce/shortcodeConfigs/liveChatMp4V2Config";

const ASSETS_API = process.env.VUE_APP_API;

function toRelativeAssetUrl(value) {
  if (!value || typeof value !== "string") return value;
  if (/^(data:|blob:)/i.test(value)) return value;

  // Already relative
  if (value.startsWith("/")) return value;

  try {
    const url = new URL(value, window.location.origin);

    // Only strip host for URLs that point to our app or assets API.
    const apiHost = ASSETS_API ? new URL(ASSETS_API).host : null;
    const isAssetsApi = apiHost ? url.host === apiHost : false;
    const isSameHost = url.host === window.location.host;

    if (isAssetsApi || isSameHost) {
      return url.pathname;
    }
  } catch (e) {
    // ignore parse errors, return original
  }

  return value;
}

function normalizeLiveChatMp4DataForSave(data) {
  if (!data || typeof data !== "object") return;

  if (data.botAvatar?.imageURL) {
    data.botAvatar.imageURL = toRelativeAssetUrl(data.botAvatar.imageURL);
  }

  if (data.waitingLoopMp4Url) {
    data.waitingLoopMp4Url = toRelativeAssetUrl(data.waitingLoopMp4Url);
  }

  traverseTree(data.messages, node => {
    if (node.content) {
      node.content = convertAssetsPathToRelativeLinks(node.content)?.body?.innerHTML;
    }

    if (node.text) {
      node.text = convertAssetsPathToRelativeLinks(node.text)?.body?.innerHTML;
    }

    if (node.value) {
      node.value = convertAssetsPathToRelativeLinks(node.value)?.body?.innerHTML;
    }

    if (node.mp4Url) {
      node.mp4Url = toRelativeAssetUrl(node.mp4Url);
    }
  });
}

function serializeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => serializeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "_telHrefError") return;
      if (key === "content") {
        const content = parseToHtml(obj[key]);
        serializeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else if (key === "text") {
        const content = parseToHtml(obj[key]);
        serializeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = serializeContent(obj[key]);
      }
    });

    return newObj;
  }
}

function visualizeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => visualizeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "content") {
        const content = parseToHtml(obj[key]);
        visualizeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else if (key === "text") {
        const content = parseToHtml(obj[key]);
        visualizeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = visualizeContent(obj[key]);
      }
    });

    return newObj;
  }
}

const editLiveChatMp4Config = (editor, shortcodeWrapper) => {
  let currentData = null;

  const liveChatWindow = editor.windowManager.openUrl({
    title: "Live Chat (MP4)",
    url: "/live-chat-mp4-dialog",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance, message) {
      const save = () => {
        editor.windowManager.alert("Success!");
        normalizeLiveChatMp4DataForSave(currentData);
        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));

        shortcodeWrapper.setAttribute("data-live-chat-mp4-data", encodedData);
        editor.fire("change");
        instance.close();
      };

      const { valid, errors } = findInvalidTelHrefsDeep(currentData);
      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then((confirmed) => {
          if (confirmed) save();
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      save();
    },
    onMessage: (instance, message) => {
      if (message.mceAction === "requestData") {
        sendData();
      }

      if (message.mceAction === "updateLiveChatData") {
        currentData = message.data;
      }

      if (message.mceAction === "transformToV2") {
        transformToV2(message.data);
      }
    },
    width: 1200,
    height: window.innerHeight - 20,
  });

  const transformToV2 = data => {
    const transform = () => {
      normalizeLiveChatMp4DataForSave(data);
      normalizeIds(data.messages);
      const serializedData = serializeContent(data);
      const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));
      const placeholder = shortcodeWrapper.querySelector(".live-chat-mp4-shortcode");
      const v2Placeholder = parseToHtml(getLiveChatMp4V2Template()).body.firstChild;

      editor.undoManager.transact(() => {
        editor.dom.setAttribs(placeholder, {
          src: v2Placeholder.getAttribute("src"),
          class: v2Placeholder.className,
        });
        shortcodeWrapper.setAttribute("data-wrapper-id", "live-chat-mp4-v2-shortcode");
        shortcodeWrapper.setAttribute("data-live-chat-mp4-data", encodedData);
      });
      editor.fire("change");
      liveChatWindow.close();
      editLiveChatMp4V2Config(editor, shortcodeWrapper);
    };

    const { valid, errors } = findInvalidTelHrefsDeep(data);
    if (!valid) {
      showTelConfirmModal(
        i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
        errors.map(e => e.invalidHrefs.join(", ")).join(", "),
      ).then(confirmed => {
        if (confirmed) transform();
        else liveChatWindow.sendMessage({ type: "telHrefValidationFailed" });
      });

      return;
    }

    transform();
  };

  const sendData = () => {
    const liveChatShortcodeData = shortcodeWrapper.getAttribute("data-live-chat-mp4-data");
    const parsedData = JSON.parse(decodeURIComponent(escape(window.atob(liveChatShortcodeData))));
    const content = visualizeContent(parsedData);

    liveChatWindow.sendMessage({
      type: "sendData",
      data: content,
    });
  };
};

const liveChatMp4Config = (tinymce, uuid, shortcodeOnClick) => {
  let currentData = null;

  tinymce.activeEditor.windowManager.openUrl({
    title: "Live Chat (MP4)",
    url: "/live-chat-mp4-dialog?data=null",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance) {
      const save = () => {
        const shortcodeWrapper = getShortcodeWrapper(
          uuid,
          shortcodeOnClick,
          getLiveChatMp4Template(),
          "live-chat-mp4-shortcode",
        );
        normalizeLiveChatMp4DataForSave(currentData);
        const shortcodeWrapperEl = parseToHtml(shortcodeWrapper).querySelector(".shortcode");
        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));

        shortcodeWrapperEl.setAttribute("data-live-chat-mp4-data", encodedData);

        addShortcodeToEditor(shortcodeWrapperEl.outerHTML, uuid);
        instance.close();
      };

      const { valid, errors } = findInvalidTelHrefsDeep(currentData);
      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then((confirmed) => {
          if (confirmed) save();
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      save();
    },
    onMessage: (instance, message) => {
      currentData = message.data;
    },
    width: 1200,
    height: window.innerHeight - 20,
  });
};

export { liveChatMp4Config, editLiveChatMp4Config };
